/**
 * 🔑 LICENSE FEATURES CONFIGURATION - 27 MODULE CATALOG
 * 
 * Defines the 27 main-gamme modules and their subsections.
 * Supports 3 license formats:
 * - ALL → All 27 modules unlocked
 * - Individual Module (e.g., "FACTURATION") → Single module
 * - CUSTOM:MODULE1:SUB1,SUB2|MODULE2:SUB3 → Custom selection
 */

import { getAgentRoutesForLicense } from '../../ai-agent/config/agentModuleRegistry';

// 🎯 MODULE KEYS (internal registry — catalog = 27 modules via LICENSE_CATALOG_MODULE_KEYS)
export const MODULES = {
  FACTURATION: 'FACTURATION',
  MANAGEMENT: 'MANAGEMENT',
  INVENTAIRE: 'INVENTAIRE',
  RESSOURCES_HUMAINES: 'RESSOURCES_HUMAINES',
  LOGISTIQUE: 'LOGISTIQUE',
  PRODUCTION: 'PRODUCTION',
  IMPORT_EXPORT: 'IMPORT_EXPORT',
  POINTAGE: 'POINTAGE',
  SALAIRES: 'SALAIRES',
  CAISSE: 'CAISSE',
  BANQUE: 'BANQUE',
  TRESORERIE: 'TRESORERIE',
  COMPTABILITE: 'COMPTABILITE',
  BUDGET: 'BUDGET',
  GESTION_SITUATION: 'GESTION_SITUATION',
  CRM: 'CRM',
  MARKETING: 'MARKETING',
  PROJETS: 'PROJETS',
  CONTRATS: 'CONTRATS',
  MAINTENANCE: 'MAINTENANCE',
  PARC_AUTO: 'PARC_AUTO',
  DISTRIBUTION: 'DISTRIBUTION',
  LIVRAISON: 'LIVRAISON',
  POINT_DE_VENTE: 'POINT_DE_VENTE',
  GED: 'GED',
  ACHATS: 'ACHATS',
  VENTES: 'VENTES',
  QUALITE: 'QUALITE',
  TACHES: 'TACHES',
  INTELLIGENCE_ARTIFICIELLE: 'INTELLIGENCE_ARTIFICIELLE',
  SERVICE_CLIENT: 'SERVICE_CLIENT',
  APPROBATIONS: 'APPROBATIONS',
  EMPLOYEE_PORTAL: 'EMPLOYEE_PORTAL',
  PLANIFICATION: 'PLANIFICATION',
  ARTICLE: 'ARTICLE',
  RELATION_FOURNISSEUR: 'RELATION_FOURNISSEUR',
  CONSTRUCTION: 'CONSTRUCTION',
  SECTOR_IMMOBILIER: 'SECTOR_IMMOBILIER',
  SECTOR_RESTAURANT: 'SECTOR_RESTAURANT',
  SECTOR_VOYAGE: 'SECTOR_VOYAGE',
  SECTOR_AGRICULTURE: 'SECTOR_AGRICULTURE',
  SECTOR_CLINIQUE: 'SECTOR_CLINIQUE',
  SECTOR_PHARMACIE: 'SECTOR_PHARMACIE',
  SECTOR_LOCATION_FLOTTE: 'SECTOR_LOCATION_FLOTTE',
  SECTOR_EDUCATION: 'SECTOR_EDUCATION',
  SECTOR_ECOMMERCE: 'SECTOR_ECOMMERCE',
};

/** Optional add-ons (sidebar + ALL license); pages filled in later. */
export const MODULE_SIDEBAR_ADDON_KEYS = [
  MODULES.SECTOR_ECOMMERCE,
  MODULES.CONSTRUCTION,
  MODULES.SECTOR_IMMOBILIER,
  MODULES.SECTOR_RESTAURANT,
  MODULES.SECTOR_VOYAGE,
  MODULES.SECTOR_AGRICULTURE,
  MODULES.SECTOR_CLINIQUE,
  MODULES.SECTOR_PHARMACIE,
  MODULES.SECTOR_LOCATION_FLOTTE,
  MODULES.SECTOR_EDUCATION,
];

/** Official Qawaem module catalog size (Console license packs). */
export const LICENSE_CATALOG_MODULE_COUNT = 27;

/**
 * Modules kept in code for legacy routes but excluded from the 27-module license catalog
 * (hidden from sidebar, settings count, and ALL_MODULES packs).
 */
export const MODULES_EXCLUDED_FROM_CATALOG = [
  MODULES.MANAGEMENT,
  MODULES.GED,
  MODULES.INTELLIGENCE_ARTIFICIELLE,
  MODULES.PRODUCTION,
  MODULES.MAINTENANCE,
  MODULES.QUALITE,
  MODULES.IMPORT_EXPORT,
  MODULES.POINT_DE_VENTE,
];

/** Ordered list of the 27 main-gamme modules — matches moduleStructure + Console. */
export const LICENSE_CATALOG_MODULE_KEYS = [
  MODULES.ARTICLE,
  MODULES.INVENTAIRE,
  MODULES.FACTURATION,
  MODULES.CRM,
  MODULES.RELATION_FOURNISSEUR,
  MODULES.ACHATS,
  MODULES.VENTES,
  MODULES.RESSOURCES_HUMAINES,
  MODULES.POINTAGE,
  MODULES.SALAIRES,
  MODULES.CAISSE,
  MODULES.BANQUE,
  MODULES.COMPTABILITE,
  MODULES.TRESORERIE,
  MODULES.BUDGET,
  MODULES.PROJETS,
  MODULES.CONTRATS,
  MODULES.PARC_AUTO,
  MODULES.LOGISTIQUE,
  MODULES.DISTRIBUTION,
  MODULES.LIVRAISON,
  MODULES.MARKETING,
  MODULES.TACHES,
  MODULES.SERVICE_CLIENT,
  MODULES.APPROBATIONS,
  MODULES.EMPLOYEE_PORTAL,
  MODULES.PLANIFICATION,
  MODULES.GESTION_SITUATION,
];

function filterCatalogModules(moduleKeys) {
  const excluded = new Set(MODULES_EXCLUDED_FROM_CATALOG);
  return moduleKeys.filter((key) => !excluded.has(normalizeModuleKey(key)));
}

/**
 * Console / demo catalogue ids → canonical keys used by moduleStructure + sidebar.
 */
export const MODULE_KEY_ALIASES = {
  ACHATS_APPROVISIONNEMENT: 'ACHATS',
  SITUATION: 'GESTION_SITUATION',
  QUALITE_CONFORMITE: 'QUALITE',
  RELATION_CLIENT: 'CRM',
  RELATION_FOURNISSEUR: 'RELATION_FOURNISSEUR',
  ARTICLES: 'ARTICLE',
};

/** Map licence / console module id → canonical MODULES key */
export function normalizeModuleKey(key) {
  if (key == null || key === '') return '';
  const raw = String(key).trim();
  const upper = raw.toUpperCase();
  if (MODULE_KEY_ALIASES[upper]) return MODULE_KEY_ALIASES[upper];
  if (Object.values(MODULES).includes(upper)) return upper;
  if (Object.keys(MODULES).includes(upper)) return MODULES[upper];
  return upper;
}

function isCanonicalModuleKey(key) {
  return Object.values(MODULES).includes(normalizeModuleKey(key));
}

// 📋 MODULE SUBSECTIONS & ROUTES
// Maps each module to its subsection routes
export const MODULE_ROUTES = {
  [MODULES.FACTURATION]: [
    '/overview',
    '/dashboard',
    '/dashboard/alertes',
    '/bord-facturation',
    '/document-creation',
    '/archives',
    '/payments',
    '/facturation/agent',
    '/facturation/alertes',
    '/facturation/devis-proformas',
    '/facturation/demande-prix',
    '/facturation/bons-commande',
    '/facturation/receptions',
    '/facturation/factures',
    '/facturation/bons-livraison',
    '/facturation/bordereaux-retour',
    '/facturation/avoirs-vente-achats',
    '/facturation/justificatifs-reglement',
    '/facturation/factures-recurrentes',
    '/facturation/facturiers',
    '/facturation/impayes',
    '/facturation/relances',
    '/facturation/conditions-paiement',
    '/facturation/numerotation',
    '/facturation/modeles-mentions',
    '/facturation/facturation-electronique',
    '/facturation/documents',
    '/facturation/analyses-rapports',
    '/facturation/config',
    '/facturation/clients-only',
    '/facturation/fournisseurs-only',
  ],

  [MODULES.ARTICLE]: [
    '/products',
    '/services',
    '/categories',
    '/articles',
    '/articles/dashboard',
    '/articles/matieres-premieres',
    '/articles/produits-semi-finis',
    '/articles/produits-finis',
    '/articles/equipements',
    '/articles/pieces-detachees',
    '/articles/unites-mesure',
    '/articles/attributs',
    '/articles/marques-fabricants',
    '/articles/variantes',
    '/articles/changelog',
    '/articles/cost-methods',
    '/articles/price-lists',
    '/articles/substituts-equivalents',
    '/articles/classification-douaniere',
    '/articles/cycle-vie',
    '/articles/documents',
    '/articles/analyses-rapports',
    '/articles/config',
  ],
  
  [MODULES.MANAGEMENT]: [
    '/overview',
    '/dashboard',
    '/clients',
    '/partners',
    '/trading-partners',
    '/employees',
    // '/employees-simple', // REMOVED - Use RH module
    // '/departments', // HIDDEN - Use RH module
    '/products',
    '/payments',
    '/returns',
    '/retours',
    '/finances'
    // '/reports' // MOVED to Facturation
  ],
  
  [MODULES.INVENTAIRE]: [
    '/overview',
    '/stock',
    '/inventaire',
    '/inventaire/dashboard',
    '/inventaire/document-creation',
    '/inventaire/reservations',
    '/inventaire/inventaire-physique',
    '/inventaire/lots-tracabilite',
    '/inventaire/reapprovisionnement',
    '/inventaire/analyses-rapports',
    '/inventaire/config',
    '/bord-inventaire',
    '/warehouses',
    '/mouvements-stock',
    '/transferts',
    '/ajustements',
    '/valorisation',
    '/achats-ventes',
    '/advanced-analytics',
    '/advanced-reports',
    '/analysis'
  ],
  
  [MODULES.RESSOURCES_HUMAINES]: [
    '/hr',
    '/employees',
    '/departments',
    '/hr/dashboard',
    '/hr/recruitment',
    '/hr/training',
    '/hr/evaluations',
    '/hr/leaves',
    '/hr/contracts',
    '/hr/departures',
    '/hr/sanctions',
    '/hr/config',
    '/hr/analyses-rapports'
  ],
  
  [MODULES.LOGISTIQUE]: [
    '/logistique',
    '/logistique/dashboard',
    '/logistique/codes-operations',
    '/logistique/tracking',
    '/logistique/route-optimization',
    '/logistique/carriers',
    '/logistique/loading-dispatch',
    '/logistique/transport-costs',
    '/logistique/consolidation',
    '/logistique/incidents-delays',
    '/logistique/transport-manifests',
    '/logistique/document-creation',
    '/logistique/archives',
    '/logistique/config'
  ],
  
  [MODULES.PRODUCTION]: [
    '/production-dashboard',
    '/production-stock',
    '/production-planning',
    '/atelier-qualite',
    '/workstation-tracking',
    '/technical-data',
    '/operators-management',
    '/production-reports'
  ],
  
  [MODULES.IMPORT_EXPORT]: [
    '/import-export',
    '/import-export/declarations-import',
    '/import-export/declarations-export',
    '/import-export/douanes',
    '/import-export/shipping',
    '/import-export/finances',
    '/import-export/compliance',
    '/import-export/reports',
    '/import-export/config'
  ],
  
  [MODULES.POINTAGE]: [
    '/timetracking',
    '/timetracking/dashboard',
    '/timetracking/daily',
    '/timetracking/weekly',
    '/timetracking/monthly',
    '/timetracking/codes',
    '/timetracking/absences',
    '/timetracking/overtime',
    '/timetracking/lateness',
    '/timetracking/delays',
    '/timetracking/planning',
    '/timetracking/team-planning',
    '/timetracking/config',
    '/timetracking/analyses-rapports',
    '/pointage/horaires-travail'
  ],

  [MODULES.SALAIRES]: [
    '/salaries',
    '/salaries/dashboard',
    '/salaries/stats',
    '/salaries/calculation',
    '/salaries/bonuses',
    '/salaries/advances',
    '/salaries/incidents',
    '/salaries/missions',
    '/salaries/virements-bancaires',
    '/salaries/config',
    '/salaries/overtime',
    '/salaries/cnas',
    '/salaries/taxes',
    '/salaries/transfers',
    '/salaries/history',
    '/salaries/analyses-rapports'
  ],
  
  [MODULES.CAISSE]: [
    '/treasury',
    '/treasury/dashboard',
    '/treasury/journal',
    '/treasury/main',
    '/treasury/closure',
    '/treasury/vouchers',
    '/treasury/reconciliation',
    '/treasury/transfers',
    '/treasury/registers',
    '/treasury/closures',
    '/treasury/z-reports',
    '/treasury/drawers',
    '/treasury/variances',
    '/treasury/sync',
    '/treasury/history',
    '/treasury/config',
    '/treasury/analyses-rapports'
  ],

  [MODULES.BANQUE]: [
    '/banking',
    '/banking/dashboard',
    '/banking/accounts',
    '/banking/payment-orders',
    '/banking/reconciliation',
    '/banking/reconciliation/general',
    '/banking/transfers',
    '/banking/checks',
    '/banking/cheques',
    '/banking/statements',
    '/banking/forecast',
    '/banking/alerts',
    '/banking/sync',
    '/banking/config',
    '/banking/analyses-rapports'
  ],
  
  [MODULES.TRESORERIE]: [
    '/tresorerie',
    '/tresorerie/dashboard',
    '/tresorerie/position',
    '/tresorerie/rapprochement',
    '/tresorerie/alertes',
    '/tresorerie/config'
  ],
  
  [MODULES.COMPTABILITE]: [
    '/comptabilite',
    '/comptabilite/dashboard',
    '/comptabilite/plan',
    '/comptabilite/chart-accounts',
    '/comptabilite/entries',
    '/comptabilite/ledger',
    '/comptabilite/grand-livre',
    '/comptabilite/balance',
    '/comptabilite/balance-generale',
    '/comptabilite/bilan',
    '/comptabilite/tax-package',
    '/comptabilite/tax-declarations',
    '/comptabilite/income-statement',
    '/comptabilite/compliance',
    '/comptabilite/reports',
    '/comptabilite/analyses-rapports',
    '/comptabilite/config'
  ],
  
  [MODULES.BUDGET]: [
    '/budget',
    '/budget/forecast',
    '/budget/tracking',
    '/budget/simulations',
    '/budget/multi-year',
    '/budget/alerts',
    '/budget/reports',
    '/budget/config'
  ],
  
  [MODULES.GESTION_SITUATION]: [
    '/situation'
  ],
  
  [MODULES.CRM]: [
    '/crm',
    '/crm/leads',
    '/crm/prospects',
    '/crm/campaigns',
    '/crm/interactions',
    '/crm/sales-goals',
    '/crm/analyses-rapports',
    '/relation-client',
    '/relation-client/clients',
    '/facturation/clients-only',
    '/crm/pipeline',
    '/crm/quotes',
    '/crm/reminders',
    '/crm/communications',
    '/crm/goals',
    '/crm/config',
  ],

  [MODULES.RELATION_FOURNISSEUR]: [
    '/relation-fournisseur',
    '/relation-fournisseur/fournisseurs',
    '/relation-fournisseur/candidats',
    '/relation-fournisseur/contacts-comptes',
    '/relation-fournisseur/contrats-accords-cadres',
    '/relation-fournisseur/evaluation-performance',
    '/relation-fournisseur/interactions',
    '/relation-fournisseur/reclamations-litiges',
    '/relation-fournisseur/conformite-certifications',
    '/relation-fournisseur/segmentation',
    '/relation-fournisseur/chronologie',
    '/relation-fournisseur/demandes-cotation',
    '/relation-fournisseur/historique-commandes',
    '/relation-fournisseur/appels-offres',
    '/relation-fournisseur/negociations',
    '/relation-fournisseur/documents',
    '/relation-fournisseur/risques',
    '/relation-fournisseur/analyses-rapports',
    '/relation-fournisseur/config',
    '/relation-fournisseur/agent',
    '/facturation/fournisseurs-only',
  ],
  
  [MODULES.MARKETING]: [
    '/marketing',
    '/marketing/campaigns',
    '/marketing/segments',
    '/marketing/emails',
    '/marketing/sms',
    '/marketing/analytics',
    '/marketing/landing-pages',
    '/marketing/ab-testing',
    '/marketing/roi',
    '/marketing/reports',
    '/marketing/config'
  ],
  
  [MODULES.PROJETS]: [
    '/projects',
    '/projects/list',
    '/projects/tasks',
    '/projects/gantt',
    '/projects/resources',
    '/projects/portfolio',
    '/projects/time-tracking',
    '/projects/milestones',
    '/projects/deliverables',
    '/projects/reports',
    '/projects/config'
  ],
  
  [MODULES.CONTRATS]: [
    '/contracts',
    '/contracts/list',
    '/contracts/templates',
    '/contracts/signatures',
    '/contracts/alerts',
    '/contracts/renewals',
    '/contracts/amendments',
    '/contracts/archive',
    '/contracts/reports',
    '/contracts/config'
  ],
  
  [MODULES.MAINTENANCE]: [
    '/maintenance',
    '/maintenance/interventions',
    '/maintenance/planning',
    '/maintenance/preventive',
    '/maintenance/curative',
    '/maintenance/technicians',
    '/maintenance/reports',
    '/maintenance/config'
  ],
  
  [MODULES.PARC_AUTO]: [
    '/fleet',
    '/fleet/vehicles',
    '/fleet/drivers',
    '/fleet/fuel',
    '/fleet/maintenance',
    '/fleet/insurance',
    '/fleet/fines',
    '/fleet/violations',
    '/fleet/gps',
    '/fleet/gps-tracking',
    '/fleet/tires',
    '/fleet/reservations',
    '/fleet/costs',
    '/fleet/reports',
    '/fleet/config',
    '/fleet/dashboard',
    '/fleet/vehicle-inspection',
    '/fleet/inspection-archive'
  ],
  
  [MODULES.DISTRIBUTION]: [
    '/distribution',
    '/distribution/routes',
    '/distribution/zones',
    '/distribution/vehicle-capacities',
    '/distribution/planning',
    '/distribution/order-preparation',
    '/distribution/delivery-teams',
    '/distribution/zone-assignments',
    '/distribution/daily-planning',
    '/distribution/collection-points',
    '/distribution/config'
  ],
  
  [MODULES.LIVRAISON]: [
    '/livraison',
    '/livraison/dashboard',
    '/livraison/ongoing',
    '/livraison/real-time-tracking',
    '/livraison/delivery-proofs',
    '/livraison/delivery-statuses',
    '/livraison/refused-deliveries',
    '/livraison/route-sheets',
    '/livraison/vehicle-loading',
    '/livraison/customer-shipments',
    '/livraison/delivery-complaints',
    '/livraison/config'
  ],
  
  [MODULES.POINT_DE_VENTE]: [
    '/pos',
    '/pos/dashboard',
    '/pos/caisse',
    '/pos/products',
    '/pos/customers',
    '/pos/suppliers',
    '/pos/stock',
    '/pos/inventory',
    '/pos/purchases',
    '/pos/wholesale',
    '/pos/payments',
    '/pos/expenses',
    '/pos/reports',
    '/pos/employees',
    '/pos/settings',
    '/pos/backup'
  ],
  
  [MODULES.GED]: [
    '/ged',
    '/ged/dashboard',
    '/ged/commercial',
    '/ged/accounting-tax',
    '/ged/customs',
    '/ged/hr-files',
    '/ged/contracts',
    '/ged/legal',
    '/ged/production-quality',
    '/ged/logistics-stock',
    '/ged/correspondence',
    '/ged/compliance-certificates'
  ],
  
  [MODULES.ACHATS]: [
    '/achats',
    '/achats/catalogue',
    '/achats/demandes',
    '/achats/commandes',
    '/achats/achats',
    '/achats/receptions',
    '/achats/retours',
    '/achats/strategie',
    '/achats/planification',
    '/achats/appels-offres',
    '/achats/negociations',
    '/achats/qualite',
    '/achats/budget',
    '/achats/comparatif-offres',
    '/achats/analyses-rapports',
    '/achats/config'
  ],
  
  [MODULES.VENTES]: [
    '/ventes',
    '/ventes/prix-remises',
    '/ventes/commandes',
    '/ventes/ventes',
    '/ventes/livraisons',
    '/ventes/retours',
    '/ventes/strategie',
    '/ventes/territoires',
    '/ventes/equipe',
    '/ventes/objectifs',
    '/ventes/pipeline',
    '/ventes/previsions',
    '/ventes/commissions',
    '/ventes/livre-des-ventes',
    '/ventes/analyses-rapports',
    '/ventes/config'
  ],
  
  [MODULES.QUALITE]: [
    '/qualite',
    '/qualite/dashboard'
  ],
  
  [MODULES.TACHES]: [
    '/taches',
    '/taches/dashboard'
  ],
  
  [MODULES.INTELLIGENCE_ARTIFICIELLE]: [
    '/facturation/agent',
    '/agent',
  ],
  
  [MODULES.SERVICE_CLIENT]: [
    '/service-client/tickets',
    '/service-client/sla',
    '/service-client/reclamations',
    '/service-client/interventions',
    '/service-client/knowledge',
    '/service-client/satisfaction',
    '/service-client/team',
    '/service-client/chat',
    '/service-client/routing',
    '/service-client/escalation',
    '/service-client/scripts',
    '/service-client/history'
  ],
  
  [MODULES.APPROBATIONS]: [
    '/approbations',
    '/approbations/dashboard',
    '/approbations/mes-approbations',
    '/approbations/workflows',
    '/approbations/creer',
    '/approbations/statuts',
    '/approbations/chaine',
    '/approbations/modeles',
    '/approbations/analyses-rapports',
    '/approbations/config'
  ],

  [MODULES.EMPLOYEE_PORTAL]: [
    '/employee-portal',
    '/employee-portal/profile',
    '/employee-portal/leaves',
    '/employee-portal/contracts',
    '/employee-portal/documents',
    '/employee-portal/training',
    '/employee-portal/performance',
    '/employee-portal/service-requests',
    '/employee-portal/announcements',
    '/employee-portal/account',
    '/employee-portal/analyses-rapports'
  ],
  
  [MODULES.PLANIFICATION]: [
    '/planification',
    '/planification/dashboard',
    '/planification/calendrier',
    '/planification/equipes',
    '/planification/ressources',
    '/planification/charge',
    '/planification/auto',
    '/planification/rendez-vous',
    '/planification/rappels',
    '/planification/utilisation',
    '/planification/config'
  ]
};

const AGENT_ROUTES_BY_MODULE = getAgentRoutesForLicense();
Object.entries(AGENT_ROUTES_BY_MODULE).forEach(([moduleKey, agentPath]) => {
  if (MODULE_ROUTES[moduleKey] && !MODULE_ROUTES[moduleKey].includes(agentPath)) {
    MODULE_ROUTES[moduleKey].push(agentPath);
  }
});

/** i18n key for a module display label (management title, then name). */
export function getModuleNameKey(moduleKey) {
  const normalized = normalizeModuleKey(moduleKey);
  if (!normalized) return '';
  return `modules.${normalized.toLowerCase()}.management_title`;
}

/** Resolve a module label via i18n; falls back to `.name` then the key path. */
export function resolveModuleDisplayName(moduleKey, t) {
  const nameKey = getModuleNameKey(moduleKey);
  if (!nameKey) return moduleKey || '';
  if (typeof t === 'function') {
    const translated = t(nameKey);
    if (translated && translated !== nameKey) return translated;
    const nameFallbackKey = nameKey.replace('.management_title', '.name');
    if (nameFallbackKey !== nameKey) {
      const fallback = t(nameFallbackKey);
      if (fallback && fallback !== nameFallbackKey) return fallback;
    }
  }
  return nameKey;
}

/** i18n keys for module labels — resolve with resolveModuleDisplayName(key, t). */
export const MODULE_NAMES = Object.freeze(
  Object.fromEntries(
    [...new Set(Object.values(MODULES))].map((key) => [key, getModuleNameKey(key)]),
  ),
);

// 🔍 HELPER: Get all routes for a module
export const getModuleRoutes = (moduleKey) => {
  return MODULE_ROUTES[moduleKey] || [];
};

// 🔍 HELPER: Check if a route belongs to a module
export const isRouteInModule = (route, moduleKey) => {
  const routes = MODULE_ROUTES[moduleKey] || [];
  return routes.some(r => route === r || route.startsWith(r + '/'));
};

// 🔍 HELPER: Get module from a route
export const getModuleFromRoute = (route) => {
  for (const [moduleKey, routes] of Object.entries(MODULE_ROUTES)) {
    if (routes.some(r => route === r || route.startsWith(r + '/'))) {
      return moduleKey;
    }
  }
  return null;
};

// 🔍 HELPER: Get all enabled modules from license
export const getEnabledModules = (license) => {
  if (!license) {
    return [];
  }

  const appType = license.appType || license.keyType;
  if (!appType) {
    return [];
  }
  
  // Case 1: ALL → catalog modules + optional Industry/Sector add-ons
  if (appType === 'ALL' || appType === 'ALL_MODULES') {
    return [...LICENSE_CATALOG_MODULE_KEYS, ...MODULE_SIDEBAR_ADDON_KEYS];
  }
  
  // Case 2: Single module (e.g., "FACTURATION")
  const normalizedSingle = normalizeModuleKey(appType);
  if (isCanonicalModuleKey(normalizedSingle)) {
    return [normalizedSingle];
  }
  
  // Case 3: CUSTOM:MODULE1:SUB1,SUB2|MODULE2:SUB3,SUB4
  if (appType.startsWith('CUSTOM:')) {
    const customPart = appType.substring(7); // Remove "CUSTOM:"
    const modules = customPart.split('|');
    return filterCatalogModules([
      ...new Set(
        modules
          .map((m) => normalizeModuleKey(m.split(':')[0]))
          .filter((k) => isCanonicalModuleKey(k)),
      ),
    ]);
  }
  
  // Legacy support: Check old format
  if (appType === 'ENTERPRISE' || appType === 'PROFESSIONNEL') {
    return [...LICENSE_CATALOG_MODULE_KEYS, ...MODULE_SIDEBAR_ADDON_KEYS];
  }
  
  if (appType === 'FACTURATION') {
    return [MODULES.FACTURATION];
  }
  
  if (appType === 'MANAGEMENT') {
    return [MODULES.FACTURATION];
  }
  
  if (appType === 'INVENTAIRE') {
    return [MODULES.FACTURATION, MODULES.INVENTAIRE];
  }
  
  // Default: No modules (should show upgrade prompt)
  return [];
};

// 🔍 HELPER: Get enabled routes from custom license
export const getEnabledRoutesFromCustomLicense = (license) => {
  if (!license || !license.appType) {
    return [];
  }
  
  const appType = license.appType;
  
  // Case 1: ALL → All routes
  if (appType === 'ALL' || appType === 'ALL_MODULES') {
    return Object.values(MODULE_ROUTES).flat();
  }
  
  // Case 2: Single module → All routes of that module
  const normalizedSingle = normalizeModuleKey(appType);
  if (isCanonicalModuleKey(normalizedSingle)) {
    return getModuleRoutes(normalizedSingle);
  }
  
  // Case 3: CUSTOM → Parse subsections
  if (appType.startsWith('CUSTOM:')) {
    const customPart = appType.substring(7);
    const modules = customPart.split('|');
    const enabledRoutes = [];
    
    for (const module of modules) {
      const moduleKey = normalizeModuleKey(module.split(':')[0]);
      if (!isCanonicalModuleKey(moduleKey)) continue;
      const subsectionsStr = module.split(':').slice(1).join(':');
      
      if (!subsectionsStr) {
        enabledRoutes.push(...getModuleRoutes(moduleKey));
      } else {
        enabledRoutes.push(...getModuleRoutes(moduleKey));
      }
    }
    
    return enabledRoutes;
  }
  
  // Legacy: Give all routes for old tiers
  if (appType === 'ENTERPRISE' || appType === 'PROFESSIONNEL') {
    return Object.values(MODULE_ROUTES).flat();
  }
  
  // Default: No routes
  return [];
};

// 🔍 HELPER: Check if user has access to a specific route
export const hasAccessToRoute = (license, route) => {
  if (!license) return false;
  
  // Always allow /settings
  if (route === '/settings' || route.startsWith('/settings/')) {
    return true;
  }
  
  const appType = license.appType;
  
  // Case 1: ALL → Allow everything
  if (appType === 'ALL' || appType === 'ALL_MODULES') {
    return true;
  }
  
  // Case 2: Single module
  const normalizedSingle = normalizeModuleKey(appType);
  if (isCanonicalModuleKey(normalizedSingle)) {
    return isRouteInModule(route, normalizedSingle);
  }
  
  // Case 3: CUSTOM
  if (appType && appType.startsWith('CUSTOM:')) {
    const enabledModules = getEnabledModules(license);
    return enabledModules.some(moduleKey => isRouteInModule(route, moduleKey));
  }
  
  // Legacy support
  if (appType === 'ENTERPRISE' || appType === 'PROFESSIONNEL') {
    return true; // Full access
  }
  
  // Default: No access
  return false;
};

// 🔍 HELPER: Check if user has access to a specific module
export const hasAccessToModule = (license, moduleKey) => {
  if (!license) return false;
  
  const enabledModules = getEnabledModules(license);
  const normalizedKey = normalizeModuleKey(moduleKey);
  return enabledModules.includes(normalizedKey);
};

export default {
  MODULES,
  MODULE_KEY_ALIASES,
  LICENSE_CATALOG_MODULE_KEYS,
  MODULES_EXCLUDED_FROM_CATALOG,
  MODULE_ROUTES,
  MODULE_NAMES,
  normalizeModuleKey,
  getModuleNameKey,
  resolveModuleDisplayName,
  getModuleRoutes,
  isRouteInModule,
  getModuleFromRoute,
  getEnabledModules,
  getEnabledRoutesFromCustomLicense,
  hasAccessToRoute,
  hasAccessToModule
};

